import { useRef, useState, useEffect, useContext } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ArrowBack, Close as CloseIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { IconButton } from '@mui/material';

import {CONFIRMED, CONVERSION_TRIGGERED, CURATION_COMPLETED, CURATION_IN_PROGRESS, EMPTY_STRING, ENTER, ERROR, FILES_LIMIT_TO_WAIT, HOME_PATH,
  JOBS, PROCESSING, RATING_COMPLETED, RATING_IN_PROGRESS, RECORDS_CREATED, RESPONSE_TO_USER, ZIP_FILE_TYPE} from '../../constants';
import { TextGray, Text, LeftBox, ButtonStretched, Heading2, LinkBlack, Part1Div, Part2Div, TwoPartDiv, Input, Checkbox, BorderedBox, FilesImg, StackDivCol, PresetsIcon, BorderedIconBox, SubHeader, StackDivRow, FileArea, UploadImg, EmailInput, SubmitButton, LoadingImg, ChatBody, MessageInput, SendImg, ChatInputArea, SendButton, SuccessBox, Chatbox, Chatheader, MessageBubble, MessageWrapper, Claim, HiddenInput, ProgressContainer, ProgressBar } from '../../styles/styles';
import { createCollectionAndTransformation, uploadFilesAddJob, validFileType } from '../../services/helperService';
import { AuthContext } from '../../providers/AuthProvider';
import { firestoreDb } from '../../firebase';
import LlmService from '../../services/llmService';
import FilesIcon from '../../imgs/files.svg';
import BlinkIcon from '../../imgs/blink.svg';
import BlurIcon from '../../imgs/blur.svg';
import UploadIcon from '../../imgs/upload.svg';
import BookTurningIcon from '../../imgs/book-turning.svg';
import SendIcon from '../../imgs/send.svg';
import LoaderIcon from '../../imgs/loader.svg';
import Alert from '../children/Alert';

const PRESETS = [
  {
    title: "Blink free",
    description: "Cureyta will identify and mark images where subjects are blinking or have their eyes closed.",
    icon: BlinkIcon
  },
  {
    title: "Blur detection",
    description: "Cureyta will identify and mark your blurry images.",
    icon: BlurIcon
  }
] as const;

const CreateCollection = () => {
  const { currentUser } = useContext(AuthContext);
  const [title, setTitle] = useState(EMPTY_STRING);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [alertIsOpen, setAlertIsOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<string>(EMPTY_STRING);
  const [alertMessage, setAlertMessage] = useState<string>(EMPTY_STRING);
  const [checkedCriteria, setCheckedCriteria] = useState([false, false]);
  const [openChat, setOpenChat] = useState<boolean>(false);
  const [jobId, setJobId] = useState(EMPTY_STRING);
  const [llmChatIsLoading, setLlmChatIsLoading] = useState(false);
  const [customCriteria, setCustomCriteria] = useState<CustomCriteria[]>();
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([]);
  const [userMsgInput, setUserMsgInput] = useState(EMPTY_STRING);
  const currentThreadId = useRef(uuidv4()).current;
  const [notificationEmail, setNotificationEmail] = useState('');
  const [showEmailBox, setShowEmailBox] = useState(false);
  const [curationProgress, setCurationProgress] = useState(0);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleCloseChat = () => {
    setOpenChat(false);
  };

  const inputFileRef = useRef(null);

  const resetUploadedFiles = () => {
    setUploadedFiles([]);
    setIsUploading(false);
    setUploadProgress(0);
  }

  const openAlertWithMsg = (type: string, msg: string) => {
    setAlertIsOpen(true);
    setAlertType(type);
    setAlertMessage(msg);
  }

  const handleZipUpload = (files: File[]) => {
    if (files.length > 1) {
      openAlertWithMsg(ERROR, "Please upload 1 zip file at a time.");
      resetUploadedFiles();
      return;
    } else if (files.length === 1) {
      // TODO: unzip, validate files, upload
      return;
    }
  }

  const handleUserMsgInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserMsgInput(e.target.value);
  }

  const handleSendMessage = async () => {
    if (userMsgInput.trim() === EMPTY_STRING) {
      return;
    }
    setUserMsgInput(EMPTY_STRING);
    const newMsgsWithUserMsg = [...chatMsgs, { content: userMsgInput, userMsg: true }]
    setChatMsgs(newMsgsWithUserMsg);
    setLlmChatIsLoading(true);
    const resObj = await LlmService.chatToVerifyPrompt(userMsgInput, currentThreadId, currentUser?.uid);
    if (!resObj) {
      openAlertWithMsg(ERROR, "Sorry, something went wrong. Please try again.");
      setLlmChatIsLoading(false);
      return;
    }
    if (RESPONSE_TO_USER in resObj) {
      setChatMsgs([...newMsgsWithUserMsg, { content: resObj?.responseToUser, userMsg: false }]);
    } else if (CONFIRMED in resObj && resObj?.confirmed) {
      const summaryObj = await LlmService.extractCriteria(currentThreadId);
      if (summaryObj && summaryObj?.length > 0) {
        setCustomCriteria(summaryObj?.map(summaryCriterion => ({
          summary: summaryCriterion?.summary,
          criterion: summaryCriterion?.criterion
        })));
      }
      setChatMsgs([...newMsgsWithUserMsg, { content: "Noted! Go ahead and upload your photos, Cureyta will take care of the rest!", userMsg: false }]);
    }
    setLlmChatIsLoading(false);
  };

  const handleEnterClickToSend = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === ENTER && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...e.target.files];
    const totalFiles: number = files?.length;
    if (totalFiles === 0) {
      openAlertWithMsg(ERROR, "Please select photos that you want to curate.");
      resetUploadedFiles();
      return;
    }
    setIsUploading(true);
    setUploadedFiles(files);
    setCurationProgress(1);
    // todo
    if (files[0].type === ZIP_FILE_TYPE) {
      handleZipUpload(files);
      return;
    }
    const fileTypesSet = new Set<string>();
    for (const file of files) {
      if (!validFileType(file.type)) {
        openAlertWithMsg(ERROR, `Sorry, we do not currently support ${file.type}. Please try again with supported image types.`);
        resetUploadedFiles();
        return;
      }
      fileTypesSet.add(file.type);
    }
    const { newCollectionId, newTransformationId, presetsPrompt } = await createCollectionAndTransformation(title, files, checkedCriteria, customCriteria, currentThreadId, currentUser?.uid);
    const jobId = await uploadFilesAddJob({ files, collectionId: newCollectionId, transformationId: newTransformationId, isPresetPrompts: presetsPrompt, fileTypesSet, setUploadProgress });
    if (!jobId) {
      openAlertWithMsg(ERROR, "Sorry, something went wrong. Please try again later.");
      resetUploadedFiles();
      return;
    }
    setIsUploading(false);
    if (totalFiles > FILES_LIMIT_TO_WAIT) {
      setShowEmailForm(true);
    }
    setJobId(jobId);
  }

  const validInputs = () => {
    if (isUploading) {
      return;
    } else if (!title.trim()) {
      openAlertWithMsg(ERROR, "Please add a title for your collection.");
      return false;
    } else if (checkedCriteria.every(criteria => !criteria) && customCriteria?.length === 0) {
      openAlertWithMsg(ERROR, "Please select at least one criterion or add a description of the photos you want to curate.");
      return false;
    }
    return true;
  }

  const handleUploadClick = () => {
    if (!validInputs()) {
      return;
    }
    const presetsPrompt: boolean = !customCriteria || customCriteria?.length === 0;
    if (presetsPrompt && !checkedCriteria.some(criteria => criteria)) {
      openAlertWithMsg(ERROR, "Please select a preset or tell Cureyta the photos you want to cull.");
      resetUploadedFiles();
      return;
    }
    inputFileRef.current.click()
  }

  const handleCriteriaChange = (index: number) => {
    const newCheckedCriteria = [...checkedCriteria];
    newCheckedCriteria[index] = !newCheckedCriteria[index];
    setCheckedCriteria(newCheckedCriteria);
  }

  const formatMessage = (userMsg: boolean, message: string, index: number, previousMessage: string) => {
    if (message === EMPTY_STRING && previousMessage !== EMPTY_STRING) {
      return (<br key={index} />);
    };
    return (<Text style={{ lineHeight: "20px", color: userMsg ? "#fff" : "#747775" }} key={index}>{message}</Text>);
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // todo: Handle email submission logic here
  };

  const handleOpenChat = () => {
    setOpenChat(true);
  };

  const handleNavigation = (path: string) => {
    if (isUploading) {
      const shouldNavigate = window.confirm('Upload in progress. If you leave now, your changes will be lost.');
      if (!shouldNavigate) {
        return;
      }
    }
    window.location.assign(path);
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUploading) {
        const message = 'Upload in progress. If you leave now, your changes will be lost.';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading]);

  useEffect(() => {
    if (!jobId) {
      return;
    }
    const unsubscribe = onSnapshot(doc(firestoreDb, JOBS, jobId), (doc) => {
      const jobData = doc.data();
      switch (jobData?.status) {
        case ERROR:
          openAlertWithMsg(ERROR, jobData?.message || "Sorry, something went wrong. Please try again later.");
          setShowEmailForm(false);
          break;
        case PROCESSING:
          setCurationProgress(2);
          break;
        case RECORDS_CREATED:
          setCurationProgress(3);
          break;
        case CONVERSION_TRIGGERED:
          setCurationProgress(4);
          break;
        case RATING_IN_PROGRESS:
          setCurationProgress(5);
          break;
        case RATING_COMPLETED:
          setCurationProgress(6);
          break;
        case CURATION_IN_PROGRESS:
          setCurationProgress(7);
          break;
        case CURATION_COMPLETED:
          setCurationProgress(8);
          window.location.assign(`/collection/${jobData?.collectionId}`);
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, [jobId]);

  return (<>
    <TwoPartDiv>
      <Part1Div>
        <LeftBox style={{ marginTop: "24px", padding: "0 24px" }}>
          <LinkBlack onClick={() => handleNavigation(HOME_PATH)} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px" }}>
            <ArrowBack style={{ fontSize: "16px" }} />My collections
          </LinkBlack>
          <Heading2 style={{ marginBottom: "24px", textAlign: "left" }}>Create new collection</Heading2>
          <Text style={{ marginBottom: "8px" }}>Title</Text>
          <Input type="text" placeholder="e.g. Wedding 2k25" value={title} onChange={(e) => setTitle(e.target.value)} style={{ marginBottom: "24px" }} />
          {openChat ? (
            <Chatbox>
              <Chatheader>
                <Text style={{ fontWeight: "500" }}>Generate custom criteria</Text>
                <IconButton onClick={handleCloseChat}>
                  <CloseIcon style={{ fontSize: "20px" }} />
                </IconButton>
              </Chatheader>
              {chatMsgs && chatMsgs?.length > 0 &&
                <ChatBody style={{ padding: "16px", overflowY: "auto" }}>
                  {[...chatMsgs].map((msg, index) => (
                    <MessageWrapper key={index} isUser={msg.userMsg}>
                      <MessageBubble isUser={msg.userMsg}>
                        {msg?.content?.split(/\r?\n/)?.map((text, i, arr) => (
                          formatMessage(msg.userMsg, text, i, arr[i - 1])
                        ))}
                      </MessageBubble>
                    </MessageWrapper>
                  ))}
                  {llmChatIsLoading && (<img src={LoaderIcon} style={{ alignSelf: "flex-start" }} />)}
                </ChatBody>
              }
              {(!chatMsgs || chatMsgs?.length === 0) &&
                <ChatBody style={{ alignItems: "center" }}>
                  <Claim>Hi, what are you looking for?</Claim>
                </ChatBody>
              }
              <ChatInputArea>
                <MessageInput rows={2} placeholder="Message Cureyta" value={userMsgInput} onChange={handleUserMsgInputChange} onKeyDown={handleEnterClickToSend} />
                <SendButton onClick={handleSendMessage}>
                  <SendImg src={SendIcon} />
                </SendButton>
              </ChatInputArea>
            </Chatbox>
          ) : (
            <>
              <Text style={{ marginBottom: "16px" }}>Choose from our presets</Text>
              {PRESETS.map((preset, index) => (
                <BorderedBox key={index} isChecked={checkedCriteria[index]} onClick={() => handleCriteriaChange(index)}>
                  <StackDivRow>
                    <BorderedIconBox>
                      <PresetsIcon src={preset.icon} alt={preset.title} />
                    </BorderedIconBox>
                    <StackDivCol>
                      <SubHeader>{preset.title}</SubHeader>
                      <TextGray style={{ fontSize: "14px" }}>{preset.description}</TextGray>
                    </StackDivCol>
                  </StackDivRow>
                  <Checkbox checked={checkedCriteria[index]} />
                </BorderedBox>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: "16px", margin: "24px 0" }}>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }} />
                <TextGray>Or</TextGray>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }} />
              </div>
              <ButtonStretched onClick={handleOpenChat} disabled={checkedCriteria.some(criteria => criteria)}
                style={{
                  marginBottom: "16px",
                  background: checkedCriteria.some(criteria => criteria) ? "rgba(29, 27, 32, 0.12)" : "#A20B91",
                  color: checkedCriteria.some(criteria => criteria) ? "#1D1B20" : "#FFF"
                }}>
                Generate custom criteria
              </ButtonStretched>
              <TextGray style={{ fontSize: "14px", textAlign: "center" }}>
                Looking for something specific? Create custom criteria.
              </TextGray>
            </>
          )}
        </LeftBox>
      </Part1Div>
      <Part2Div>
        <LeftBox style={{ marginTop: "24px", padding: "0 24px" }}>
          {(isUploading || curationProgress > 0) &&
            <div style={{ maxWidth: "400px", margin: "0 auto", padding: "24px" }}>
              <LoadingImg src={BookTurningIcon} alt="Curation in progress" />
              <ProgressContainer>
                <Text>Curation in progress...</Text>
                <ProgressBar progress={uploadProgress} />
                <TextGray style={{ fontSize: "14px" }}>
                  {curationProgress > 0 ? curationProgress : ""}
                </TextGray>
              </ProgressContainer>
              {uploadedFiles.length > 0 && <Text style={{ marginTop: '8px' }}>{uploadedFiles.length} files selected</Text>}
              {showEmailForm &&
                <div>
                  <Heading2 style={{ fontSize: "20px", marginBottom: "8px", textAlign: "left" }}>Come back later</Heading2>
                  <TextGray style={{ fontSize: "14px", marginBottom: "16px" }}>
                    Leave your email and we'll notify you once your photos have been curated.
                  </TextGray>
                  <form onSubmit={handleEmailSubmit}>
                    <EmailInput
                      type="email"
                      placeholder="Email..."
                      value={notificationEmail}
                      onChange={(e) => setNotificationEmail(e.target.value)}
                      required
                    />
                    <SubmitButton type="submit">
                      Submit
                    </SubmitButton>
                  </form>
                  {showEmailBox && (
                    <SuccessBox>
                      <div style={{ position: "absolute", top: "8px", right: "12px", cursor: "pointer" }}>
                        <CloseIcon style={{ fontSize: "20px" }} onClick={() => setShowEmailBox(false)} />
                      </div>
                      <Text>Email Submitted!</Text>
                      <Text>We'll notify you once your photos have been curated.</Text>
                    </SuccessBox>
                  )}
                </div>
              }
            </div>
          }
          {!isUploading && curationProgress === 0 && <>
            <Heading2 style={{ marginBottom: "8px", textAlign: "left" }}>Upload your images</Heading2>
            <TextGray style={{ marginBottom: "24px" }}>Supported formats: jpg, png, heic, webp</TextGray>
            <FileArea onClick={handleUploadClick}>
              <div style={{ position: 'relative' }}>
                <FilesImg src={FilesIcon} />
                <UploadImg src={UploadIcon} />
              </div>
              <Text>Drag and Drop file here, or <span style={{ color: "#A20B91" }}>Choose file</span></Text>
              <HiddenInput id='fileUpload' onChange={handleUploadFiles} ref={inputFileRef} type='file' multiple accept='.heic,image/jpeg,image/png,image/webp,.zip' />
            </FileArea>
          </>}
          <div style={{ marginTop: "24px", padding: "16px", backgroundColor: "#FFF", borderRadius: "16px", display: "flex", gap: "12px", border: "1px solid #D9D9D9" }}>
            <span role="img" aria-label="info" style={{ fontSize: "20px" }}>💡</span>
            <div>
              <Text style={{ fontWeight: "500", marginBottom: "4px" }}>Data and Privacy</Text>
              <TextGray style={{ fontSize: "14px" }}>
                Any photos you upload are processed securely and never used beyond their intended purpose.
              </TextGray>
            </div>
          </div>
        </LeftBox>
      </Part2Div>
    </TwoPartDiv>
    <Alert alertType={alertType} alertIsOpen={alertIsOpen} alertMessage={alertMessage} setAlertIsOpen={setAlertIsOpen} />
  </>
  );
};

export default CreateCollection;
